import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap } from "../client";

/**
 * A safety net only: live updates (app/live) refetch the bell within seconds of a change. This poll
 * covers a tab whose live connection is down.
 */
const POLL_MS = 60_000;

export const notificationKeys = {
  all: (slug: string) => ["tenant", slug, "notifications"] as const,
  unread: (slug: string) => ["tenant", slug, "notifications", "unread"] as const,
  categories: (slug: string) => ["tenant", slug, "notification-categories"] as const,
};
const keys = notificationKeys;

export function useUnreadNotifications(slug: string) {
  return useQuery({
    queryKey: keys.unread(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/notifications/unread/", { params: { path: { tenant_slug: slug } } })),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** The member's notifications in this workspace, latest first (fetched while the list is open).
 * `actions` lists only the open action-required ones. */
export function useNotifications(slug: string, { enabled, pageSize, actions = false }: { enabled: boolean; pageSize: number; actions?: boolean }) {
  return useQuery({
    queryKey: [...keys.all(slug), "list", actions ? "actions" : "all", pageSize],
    enabled,
    queryFn: () =>
      unwrap(
        api.GET("/api/t/{tenant_slug}/notifications/", {
          params: { path: { tenant_slug: slug }, query: { page_size: pageSize, ...(actions ? { actions: true } : {}) } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** The categories a member can turn on or off, and whether this installation can email. */
export function useNotificationCategories(slug: string) {
  return useQuery({
    queryKey: keys.categories(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/notifications/categories/", { params: { path: { tenant_slug: slug } } })),
    staleTime: 10 * 60_000,
  });
}

export function useMarkNotificationRead(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST("/api/t/{tenant_slug}/notifications/{id}/read/", { params: { path: { tenant_slug: slug, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all(slug) }),
  });
}

export function useMarkAllNotificationsRead(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/t/{tenant_slug}/notifications/read-all/", { params: { path: { tenant_slug: slug } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all(slug) }),
  });
}
